/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useEffect } from 'react';
// redux
import { useSelector } from 'react-redux';
// material
import { ListSelectDropDown, ExportLink, themeV2 } from '@insticator/insticator-ui';
import LoadingState from '../../../../material/states/loading/loading';
import NoDataState from '../../../../material/states/nodata/nodata';
import LinkControls from '../../../../material/cardwrapper/linkcontrols/linkcontrols';
import CardWrapper from '../../../../material/cardwrapper/cardwrapper';
import ChartTable from './charttable/charttable';
import StackedCharts from './stackedcharts/stackedcharts';
import {chartTableColors} from "../../../../utils/charts";
// utils
import {
    formatTableDate,
    formatNumber,
    formatXAxisDates,
    getChartCardRateDirection,
    getEnabledRollUps,
    getProductDetails,
    downloadCSV
} from '../../../../utils/charts';
// styles
import injectSheet from 'react-jss';
import styles from './revenuedrivers_styles';

const chartColors = {
    revenue: chartTableColors[0],
    rpm: chartTableColors[5],
    unitLoad: chartTableColors[6]
}
/* ========== ~~~~~~~~~~ REVENUE DRIVERS ~~~~~~~~~~ ========== */
const RevenueDrivers = injectSheet(styles)((props) => {
    const { details, status, overview } = useSelector((state) => state.revenue);
    const { filters } = useSelector((state) => state.app);

    const [currentChartValues, setCurrentChartValues] = useState({revenue: 0, rpm: 0, unitLoad: 0});
    const [selectedPeriod, setSelectedPeriod] = useState('daily');
    const period = `${selectedPeriod[0].toUpperCase()}${selectedPeriod.slice(1)}`; // Daily, Monthly, Weekly, Quarterly
    const [periodText, setPeriodText] = useState('');
    const [showTotal, setShowTotal] = useState(true);
    const [enabledRollups, setEnabledRollups] = useState([]);
    const currentTotalDetail =  Object.keys(details['TOTAL'][period].TOTAL);
    const productList = Object.keys(details['TOTAL'][period]).filter(id => id !== 'TOTAL').map(id => ({value: id, name: getProductDetails(id).name, selected: true}))
    const [currentProducts, setCurrentProducts] = useState(productList.map(product => product.value));


    useEffect(() => {
        if(showTotal) {
            const chartValues = {
                revenue: 0,
                rpm: 0,
                unitLoad: 0,
                revenueChangeRate: 0,
                rpmChangeRate: 0,
                unitLoadChangeRate: 0
            }

            if(currentProducts.length > 0) {
                currentProducts.forEach(productId => {
                    Object.keys(chartValues).forEach(metricKey => {
                        chartValues[metricKey] += overview['TOTAL'][productId] && overview['TOTAL'][productId][metricKey] ? overview['TOTAL'][productId][metricKey] : 0;
                    });
                })
                if(currentProducts.length > 1) chartValues['rpm'] = (chartValues['revenue'] / chartValues['unitLoad']) * 1000;
                setCurrentChartValues(chartValues);
            } else {
                setCurrentChartValues({
                    revenue: overview.TOTAL.revenue ? overview.TOTAL.revenue : 0,
                    rpm: overview.TOTAL.rpm ? overview.TOTAL.rpm : 0,
                    unitLoad: overview.TOTAL.unitLoad ? overview.TOTAL.unitLoad : 0,
                    revenueChangeRate: overview.TOTAL.revenueChangeRate ? overview.TOTAL.revenueChangeRate : 0,
                    rpmChangeRate: overview.TOTAL.rpmChangeRate ? overview.TOTAL.rpmChangeRate : 0,
                    unitLoadChangeRate: overview.TOTAL.unitLoadChangeRate ? overview.TOTAL.unitLoadChangeRate : 0
                })
            }
            setPeriodText('');
        }
    }, [overview, showTotal, currentProducts]);

    useEffect(() => {
        if (productList) setCurrentProducts(productList.map(product => product.value));
    }, [details])

    useEffect(() => {
        if(status === 'fulfilled' && enabledRollups.length === 0) {
            const rollups = getEnabledRollUps(details['TOTAL']);
            setEnabledRollups(rollups);
            if(rollups.length > 0) setSelectedPeriod(rollups[0]);
        } else {
            setEnabledRollups([]);
            setShowTotal(true);
            setSelectedPeriod('daily');
        }
    }, [status]);

    const revenueDrivers = [
      { id: 'revenue', color: chartColors.revenue, title: 'Revenue'},
      { id: 'rpm', color: chartColors.rpm, title: 'Unit Load RPM'},
      { id: 'unitLoad', color: chartColors.unitLoad,  title: 'Unit Loads'}
    ];

    const formatStackedChartData = (details, period) => {
        const detailsTotalData = details.TOTAL[period].TOTAL;
        const dateLength = Object.keys(detailsTotalData).length;

        const datasets = [
            { id: 'revenue', data: [{id: 'revenue', color: chartColors.revenue, data: []}], color: chartColors.revenue, title: 'Revenue'},
            { id: 'rpm', data: [{id: 'rpm', color: chartColors.rpm, data: []}], color: chartColors.rpm, title: 'Unit Load RPM'},
            { id: 'unitLoad', data: [{id: 'unitLoad', color: chartColors.unitLoad, data: []}], color: chartColors.unitLoad,  title: 'Unit Loads'}
        ];

        Object.keys(detailsTotalData).forEach((date, index) => {
            datasets.forEach(dataset => {
                let y = 0;
                currentProducts.forEach(productId => {
                    const productTotal = details.TOTAL[period][productId];
                    const productTotalPerDate =  productTotal ? productTotal[date] : 0;
                    y += productTotalPerDate[dataset.id]
                })
                // To allow duplicate x-axis labels // TODO: Refactor this
                let spaces = '';
                for(var i = 0; i < index; i++) spaces += ' ';
                dataset.data[0].data.push({
                    x: formatXAxisDates(date, period, dateLength, 10).concat(spaces),
                    y:  currentProducts.length > 0 ? y : detailsTotalData[date][dataset.id],
                    tableDate: formatTableDate(date, period, detailsTotalData[date].startDate, detailsTotalData[date].endDate),
                    origDate: date,
                    [`${dataset.id}ChangeRate`]: detailsTotalData[date][`${dataset.id}ChangeRate`]
                });
            })
        });

        // compute the rpm based on the selected products total revenue and unitload
        if(datasets[1].data[0].data.length > 0) {
            datasets[1].data[0].data = datasets[1].data[0].data.map((rpmChartData, index) => {
                const revenueChartData = datasets[0].data[0].data;
                const unitLoadChartData = datasets[2].data[0].data;
                const revenueY = revenueChartData.length > 0 ? revenueChartData[index].y : 0;
                const unitLoadY = unitLoadChartData.length > 0 ? unitLoadChartData[index].y : 0;
                return {
                    ...rpmChartData,
                    y: revenueY === 0 && unitLoadY === 0 ? 0 : (revenueY / unitLoadY) * 1000
                }
            })
        }
        return datasets;
    }

    const handleChartMouseMove = (data, period) => {
        setShowTotal(false);
        setCurrentChartValues(data);
        setPeriodText(period);
    }

    const controlOptions = [
        {"text":"Daily","value":"daily","disabled": !enabledRollups.includes('daily')},
        {"text":"Weekly","value":"weekly","disabled": !enabledRollups.includes('weekly')},
        {"text":"Monthly","value":"monthly","disabled": !enabledRollups.includes('monthly')},
        {"text":"Quarterly","value":"quarterly","disabled": !enabledRollups.includes('quarterly')}
    ];

    const isNoData = () => {
        return  (status === 'rejected') ||
                (status === 'fulfilled' && currentTotalDetail.length === 0) ||
                (status === 'fulfilled' && overview['TOTAL'].revenue === 0 && overview['TOTAL'].rpm === 0 && overview['TOTAL'].unitLoad === 0);
    }
    const isShowData = () => status === 'fulfilled' && !isNoData();
    const isLoading = () => status === 'pending';

    const stackedChartData = formatStackedChartData(details, period);

    const convertDataToCSV = chartData => {
        let csvData = 'Product,Period,Revenue,RPM,Unit Loads';
        let fileName = 'Revenue Drivers Data';

        const dates = chartData[0].data[0].data.map(chartData => chartData.x);
        const origDates = chartData[0].data[0].data.map(chartData => chartData.origDate);
        csvData += "\n";

        dates.forEach((date, index) => {
            const products = currentProducts.length > 0 ? currentProducts : ['TOTAL'];
            products.forEach(productId => {
                const productDetailsPerPeriod = details['TOTAL'][period][productId];
                const origDate = origDates[index];

                const revenueCell = formatNumber(productDetailsPerPeriod[origDate].revenue, 'dollar').replaceAll(',','');
                const rpmCell = formatNumber(productDetailsPerPeriod[origDate].rpm, 'dollar').replaceAll(',','');
                const unitLoadCell = formatNumber(productDetailsPerPeriod[origDate].unitLoad).replaceAll(',','');

                const productName = getProductDetails(productId).name !== 'Total' ? getProductDetails(productId).name : 'All';

                csvData += `${productName},${period == 'Daily' ? origDate : date},${revenueCell},${rpmCell},${unitLoadCell}`;
                csvData += "\n";
            })
        })
        // set the file name
        fileName += ` ${period} `;
        fileName = dates.length > 1 ? `${fileName} - ${dates[0]} - ${dates[dates.length - 1]}` : fileName;
        return {
            data: csvData,
            fileName
        }
    }

    const handleExport = () => {
        const csvData = convertDataToCSV(stackedChartData);
        downloadCSV(csvData.data, `${csvData.fileName}.csv`, 'text/csv;encoding:utf-8');
    }

    return (
        <CardWrapper klass={`revenuedrivers ${props.classes.revenuedrivers}`} tooltipContent={props.tooltipContent}>
            { isLoading() && <LoadingState />}
            { isNoData() && <NoDataState title="Revenue Drivers" />}
            { isShowData() &&
                <div className={'data'}>
                    <div className={'responsiveline'}>
                        <div className={'driverheader'}>
                            <div className={'title title_date'}>{props.title}</div>
                            <ListSelectDropDown
                                klass={'productdropdown'}
                                noEmptyApply={true}
                                buttonAction={items => setCurrentProducts(items['undefined'])}
                                items={productList}
                                selectedItems={currentProducts}
                            />
                        </div>
                            <>
                            <div className={'chartcontainer'}>
                                <StackedCharts
                                    period={period}
                                    datasets={stackedChartData}
                                    onMouseLeave={() => setShowTotal(true)}
                                    onMouseMove={handleChartMouseMove}
                                    reset={showTotal}
                                />
                            </div>
                                <div className={'rollup'}>
                                    <LinkControls
                                        options={controlOptions}
                                        selected={selectedPeriod}
                                        handleSelect={value => {
                                            setShowTotal(true);
                                            setSelectedPeriod(value);
                                        }}
                                    />
                                </div>
                            </>
                    </div>
                    <div className={'table'}>
                        <div className={'daterange title_date'}>{periodText}</div>
                        { revenueDrivers.map(chart =>
                            <ChartTable
                                title={chart.title}
                                titleColor={chart.color}
                                value={formatNumber(currentChartValues[chart.id], chart.id !== 'unitLoad' ? 'dollar' : '')}
                                changeDirection={getChartCardRateDirection(currentChartValues[`${chart.id}ChangeRate`])}
                                change={formatNumber(currentChartValues[`${chart.id}ChangeRate`], 'percent')}
                                changeType={filters.compareTo}
                            />
                        )}
                        <div className={'exportbutton'}>
                          <ExportLink action={handleExport} processing={false} />
                        </div>
                    </div>
                </div>
            }
        </CardWrapper>
    );
});


/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default RevenueDrivers;
